import { FormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import {
  AzzurroEventAlertCodes,
  AzzurroEventDefaultConfig,
  EventModel,
  EventNotificationItem,
  GenerationEnum,
  ProductModelService,
} from 'ngx-core-services';

import { SystemDefinition } from '../../store';
import { updateEventModelsTranslations } from './event-utils';

export type EventModelsTranslations = ReturnType<typeof updateEventModelsTranslations>;

export type EventFormField = keyof AzzurroEventDefaultConfig;
export type TimeUnit = 'minutes' | 'hours' | 'days';
export type TimeFieldRange = { min: number; max: number };

export enum EventGeneration {
  G1 = 'G1',
  G2 = 'G2',
  G3 = 'G3',
  GPM = 'GPM',
}

export const TIME_FIELD_RANGES: Partial<Record<EventFormField, TimeFieldRange>> = {
  activeTimeMinutes: { min: 5, max: 720 },
  recurringEmailIntervalMinutes: { min: 720, max: 1440 },
  snoozeDurationMinutes: { min: 720, max: 1440 },
};

export const getSystemEventsTypes = (system: SystemDefinition | undefined) => {
  const type: GenerationEnum[] = [];
  const devices = system?.devices.map((device) => new ProductModelService(device.id));
  if (devices?.some((device) => device.isProductA || device.isProductB)) {
    type.push(GenerationEnum.G1_ZCS);
  }

  if (devices?.some((device) => device.isProductC && !device.isPowerMagic)) {
    type.push(GenerationEnum.G2_ZCS);
  }

  if (devices?.some((device) => device.isProductD && !device.isPowerMagic)) {
    type.push(GenerationEnum.G3_ZCS);
  }

  if (devices?.some((device) => device.isPowerMagic)) {
    type.push(GenerationEnum.GPM_ZCS);
  }

  return type;
};

export const getDevicesByCode = (alertCodes: AzzurroEventAlertCodes | undefined, system: SystemDefinition) => {
  const devicesByGeneration = system.metadata?.verifiedDevices?.map((device) => {
    const product = new ProductModelService(device.sn);
    const generation = product.isPowerMagic
      ? EventGeneration.GPM
      : product.isProductA || product.isProductB
        ? EventGeneration.G1
        : product.isProductC
          ? EventGeneration.G2
          : product.isProductD
            ? EventGeneration.G3
            : undefined;
    const code = generation === EventGeneration.G1
      ? alertCodes?.G1_ZCS
      : generation === EventGeneration.G2
        ? alertCodes?.G2_ZCS
        : generation === EventGeneration.G3
          ? alertCodes?.G3_ZCS
          : undefined;

    return {
      serial: device.sn,
      generation,
      code,
    };
  });

  const devicesByAzzurroEvent = [];
  const devicesByG1Generation = devicesByGeneration?.filter((device) => device.generation === EventGeneration.G1);
  const devicesByG2Generation = devicesByGeneration?.filter((device) => device.generation === EventGeneration.G2);
  const devicesByG3Generation = devicesByGeneration?.filter((device) => device.generation === EventGeneration.G3);
  if (alertCodes?.G1_ZCS && devicesByG1Generation?.length) {
    devicesByAzzurroEvent.push({ generation: EventGeneration.G1, devices: devicesByG1Generation });
  }

  if (alertCodes?.G2_ZCS && devicesByG2Generation?.length) {
    devicesByAzzurroEvent.push({ generation: EventGeneration.G2, devices: devicesByG2Generation });
  }

  if (alertCodes?.G3_ZCS && devicesByG3Generation?.length) {
    devicesByAzzurroEvent.push({ generation: EventGeneration.G3, devices: devicesByG3Generation });
  }

  if (devicesByAzzurroEvent.length === 0) {
    return null;
  }

  return devicesByAzzurroEvent.map((generation) => {
    return generation.devices.map((device, index) => {
      return `${index === 0 ? '' : ', '}${index === 0 ? ('ID' + device.code + ' - ') : ''}${device.serial}`;
    }).join('');
  });
};

export const getConfiguredEventModels = (events?: Partial<EventNotificationItem> | null): EventModel[] => {
  if (!events) {
    return [];
  }
  return Object.entries(events)
    .filter(([key, value]) => key !== '__typename' && value != null)
    .map(([key]) => key as EventModel);
};

export const getEventPayload = (
  events: Partial<EventNotificationItem> | null | undefined,
  eventName: EventModel
): Partial<AzzurroEventDefaultConfig> | null => {
  if (!events) {
    return null;
  }
  return (events as Partial<Record<EventModel, Partial<AzzurroEventDefaultConfig>>>)[eventName] ?? null;
};

export interface NotificationConfigurationListItem {
  eventModelName?: EventModel;
  deviceByCode: string[] | null;
  payloadType?: string;
  defaultConfig?: AzzurroEventDefaultConfig;
  alertCodes?: AzzurroEventAlertCodes;
}

export interface NotificationEventRow {
  eventName?: EventModel;
  deviceByCode: string[] | null;
  payloadType?: string;
  defaultConfig?: AzzurroEventDefaultConfig;
  form: FormGroup;
  selected$: BehaviorSubject<boolean>;
  toggleSelection: () => void;
}

export interface ConfiguredEventRow {
  eventName: EventModel;
  form: FormGroup;
}
